/**
 * ErrorHandler - Global error handling and recovery
 *
 * Catches runtime errors, displays user-friendly messages,
 * and provides recovery mechanisms for the game.
 */

export class ErrorHandler {
  constructor() {
    this.errors = [];
    this.maxErrors = 10;
    this.errorDisplay = null;
    this.isGameRunning = true;
    this.onError = null; // Callback for custom error handling
    this.onRecovery = null; // Callback when user recovers from error

    this.setupGlobalHandlers();
    this.createErrorDisplay();
  }

  /**
   * Set up global error handlers
   */
  setupGlobalHandlers() {
    // Catch runtime errors
    window.addEventListener('error', (event) => {
      this.handleError({
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        type: 'Runtime Error'
      });
      event.preventDefault();
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        type: 'Unhandled Promise Rejection'
      });
      event.preventDefault();
    });
  }

  /**
   * Create error display UI
   */
  createErrorDisplay() {
    const display = document.createElement('div');
    display.id = 'error-display';
    display.className = 'error-display hidden';
    display.innerHTML = `
      <div class="error-content">
        <div class="error-header">
          <span class="error-icon">⚠️</span>
          <h2>Oops! Something went wrong</h2>
        </div>
        <div class="error-body">
          <p class="error-message"></p>
          <details class="error-details">
            <summary>Technical Details</summary>
            <pre class="error-stack"></pre>
          </details>
        </div>
        <div class="error-actions">
          <button class="error-btn error-btn-primary" id="error-reload">
            🔄 Reload Game
          </button>
          <button class="error-btn error-btn-secondary" id="error-continue">
            ▶️ Try to Continue
          </button>
          <button class="error-btn error-btn-secondary" id="error-dismiss">
            ✕ Dismiss
          </button>
        </div>
        <p class="error-footer">
          If this problem persists, try clearing your browser cache or reloading the page.
        </p>
      </div>
    `;

    document.body.appendChild(display);
    this.errorDisplay = display;

    // Set up button handlers
    document.getElementById('error-reload').addEventListener('click', () => {
      window.location.reload();
    });

    document.getElementById('error-continue').addEventListener('click', () => {
      this.hideError();
      if (this.onRecovery) {
        this.onRecovery();
      }
    });

    document.getElementById('error-dismiss').addEventListener('click', () => {
      this.hideError();
    });
  }

  /**
   * Handle an error
   */
  handleError(errorInfo) {
    console.error('[ErrorHandler]', errorInfo);

    // Store error
    this.errors.push({
      ...errorInfo,
      timestamp: Date.now()
    });

    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Show error display
    this.showError(errorInfo);

    // Call custom error handler if provided
    if (this.onError) {
      this.onError(errorInfo);
    }
  }

  /**
   * Show error display
   */
  showError(errorInfo) {
    if (!this.errorDisplay) return;

    const messageEl = this.errorDisplay.querySelector('.error-message');
    const stackEl = this.errorDisplay.querySelector('.error-stack');

    // Set user-friendly message
    let userMessage = errorInfo.message || 'An unknown error occurred';

    // Make common errors more user-friendly
    if (userMessage.includes('undefined')) {
      userMessage = 'The game encountered an unexpected value. This might be a temporary issue.';
    } else if (userMessage.includes('null')) {
      userMessage = 'A required game resource was not found. Try reloading the page.';
    } else if (userMessage.includes('NetworkError') || userMessage.includes('fetch')) {
      userMessage = 'Failed to load a game resource. Check your internet connection.';
    }

    messageEl.textContent = userMessage;

    // Set technical details
    let technicalDetails = '';
    if (errorInfo.type) {
      technicalDetails += `Type: ${errorInfo.type}\n`;
    }
    if (errorInfo.message) {
      technicalDetails += `Message: ${errorInfo.message}\n`;
    }
    if (errorInfo.filename) {
      technicalDetails += `File: ${errorInfo.filename}:${errorInfo.line}:${errorInfo.column}\n`;
    }
    if (errorInfo.stack) {
      technicalDetails += `\nStack Trace:\n${errorInfo.stack}`;
    }

    stackEl.textContent = technicalDetails || 'No additional details available';

    // Show the display
    this.errorDisplay.classList.remove('hidden');
  }

  /**
   * Hide error display
   */
  hideError() {
    if (this.errorDisplay) {
      this.errorDisplay.classList.add('hidden');
    }
  }

  /**
   * Wrap a function with error handling
   */
  wrap(fn, context = null) {
    return (...args) => {
      try {
        return fn.apply(context, args);
      } catch (error) {
        this.handleError({
          message: error.message,
          stack: error.stack,
          type: 'Caught Exception'
        });
        return null;
      }
    };
  }

  /**
   * Wrap an async function with error handling
   */
  wrapAsync(fn, context = null) {
    return async (...args) => {
      try {
        return await fn.apply(context, args);
      } catch (error) {
        this.handleError({
          message: error.message,
          stack: error.stack,
          type: 'Async Exception'
        });
        return null;
      }
    };
  }

  /**
   * Get all logged errors
   */
  getErrors() {
    return this.errors;
  }

  /**
   * Clear all logged errors
   */
  clearErrors() {
    this.errors = [];
  }

  /**
   * Check if there are recent errors
   */
  hasRecentErrors(withinMs = 5000) {
    if (this.errors.length === 0) return false;
    const latestError = this.errors[this.errors.length - 1];
    return (Date.now() - latestError.timestamp) < withinMs;
  }
}
